import React, { Ref } from 'react';
import { Scrollbars, positionValues as IPositionValues } from 'react-custom-scrollbars';
// Helpers
import { getGlobalCssVariables, hexToRgb } from '../../helpers/general';

interface IScrollViewProps {
    children: React.ReactNode;
    onScrollValue?: (data: IPositionValues) => void;
    className?: string;
    style?: object;
    viewStyle?: object;
    verticalOnly?: boolean;
    tabIndex?: number; // It is needed to fix tabOrder in FireFox
}

export const ScrollView = React.forwardRef(function ScrollView(props: IScrollViewProps, ref?: Ref<Scrollbars>) {
    const [colorDarkestBlue] = getGlobalCssVariables(['--color-darkest-blue']);

    const renderThumb = ({ style, ...props }: { style: object }): JSX.Element => {
        const thumbStyle = {
            backgroundColor: `rgba(${hexToRgb(colorDarkestBlue)}, 0.4)`,
            borderRadius: 4,
            width: 8,
        } as React.CSSProperties;

        return <div {...props} style={{ ...style, ...thumbStyle }} />;
    };

    const renderTrack = ({ style, ...props }: { style: object }): JSX.Element => {
        const trackStyle = {
            backgroundColor: `rgba(${hexToRgb(colorDarkestBlue)}, 0.1)`,
            width: 8,
            right: 4,
            bottom: 4,
            top: 4,
            borderRadius: 4,
            position: 'absolute',
            zIndex: 99,
        } as React.CSSProperties;

        return <div {...props} style={{ ...style, ...trackStyle }} />;
    };

    const onScroll = (data: IPositionValues): void => {
        props.onScrollValue && props.onScrollValue(data);
    };

    const renderEmpty = (): JSX.Element => {
        return <div />;
    };

    const renderView = (viewProps: any, { viewStyle, className, tabIndex }: IScrollViewProps): JSX.Element => {
        return (
            <div
                {...viewProps}
                tabIndex={tabIndex}
                className={className || ''}
                style={{
                    ...viewProps.style,
                    ...(viewStyle ? viewStyle : {}),
                    ...{ WebkitOverflowScrolling: 'unset' },
                }}
            />
        );
    };

    return (
        <Scrollbars
            ref={ref}
            renderView={(viewProps: any) => renderView(viewProps, props)}
            renderThumbVertical={renderThumb}
            {...(props.verticalOnly ? { renderTrackHorizontal: renderEmpty } : {})}
            renderTrackVertical={renderTrack}
            hideTracksWhenNotNeeded
            onScrollFrame={onScroll}
            style={props.style || {}}
        >
            {props.children}
        </Scrollbars>
    );
});
